import React from 'react';
import { ExclamationIcon, InformationCircleIcon } from '@heroicons/react/outline';

const CancellationPolicyNotice = ({ variant = 'warning', showFullPolicy = false }) => {
  const policyPoints = [
    { time: 'More than 48 hours before session', fee: 'Free cancellation', condition: 'if cancelled within 24 hours of booking' },
    { time: '12-48 hours before session', fee: '10% cancellation fee' },
    { time: '6-12 hours before session', fee: '20% cancellation fee' },
    { time: 'Less than 6 hours before session', fee: '30% cancellation fee' },
    { time: 'After session start time', fee: 'No refund available' }
  ];

  if (variant === 'compact') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <ExclamationIcon className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="ml-3">
            <h4 className="text-sm font-semibold text-amber-900">Cancellation Policy</h4>
            <p className="text-sm text-amber-800 mt-1">
              Free cancellation if more than 48 hours in advance and within 24 hours of booking. 
              Cancellation fees apply: 10% (12-48hrs), 20% (6-12hrs), 30% (less than 6hrs).
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'warning') {
    return (
      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
        <div className="flex items-start">
          <ExclamationIcon className="h-6 w-6 text-yellow-600 mt-1 flex-shrink-0" />
          <div className="ml-3 flex-1">
            <h3 className="text-base font-semibold text-yellow-900 mb-2">
              Important: Cancellation Policy
            </h3>
            <p className="text-sm text-yellow-800 mb-3">
              By proceeding with this booking, you agree to our cancellation policy. Please review the fees below:
            </p>
            <div className="bg-white bg-opacity-50 rounded p-3">
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span className="text-yellow-900">
                    <strong>Free cancellation</strong> if more than 48 hours away AND within 24 hours of booking
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-600 mr-2">•</span>
                  <span className="text-yellow-900">
                    <strong>10% fee</strong> for cancellations 12-48 hours before session
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 mr-2">•</span>
                  <span className="text-yellow-900">
                    <strong>20% fee</strong> for cancellations 6-12 hours before session
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">•</span>
                  <span className="text-yellow-900">
                    <strong>30% fee</strong> for cancellations less than 6 hours before session
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-700 mr-2">✗</span>
                  <span className="text-yellow-900">
                    <strong>No refund</strong> after session start time
                  </span>
                </li>
              </ul>
            </div>
            <p className="text-xs text-yellow-700 mt-3 italic">
              Refunds are processed automatically and typically appear within 5-10 business days.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Full policy view
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center mb-4">
        <InformationCircleIcon className="h-6 w-6 text-blue-600 mr-2" />
        <h2 className="text-xl font-bold text-gray-900">Cancellation & Refund Policy</h2>
      </div>
      
      <div className="prose prose-sm max-w-none text-gray-700">
        <p className="mb-4">
          We understand that plans can change. Our cancellation policy is designed to be fair 
          to both customers and Security Service Providers while ensuring service reliability.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">Cancellation Fees</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time Before Session
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cancellation Fee
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Refund Amount
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {policyPoints.map((point, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {point.time}
                    {point.condition && (
                      <span className="block text-xs text-gray-500">{point.condition}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`font-medium ${
                      point.fee.includes('Free') ? 'text-green-600' :
                      point.fee.includes('No refund') ? 'text-red-600' :
                      'text-yellow-600'
                    }`}>
                      {point.fee}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {point.fee.includes('Free') ? '100% refund' :
                     point.fee.includes('10%') ? '90% refund' :
                     point.fee.includes('20%') ? '80% refund' :
                     point.fee.includes('30%') ? '70% refund' :
                     'No refund'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold mt-6 mb-3">Important Notes</h3>
        <ul className="list-disc pl-5 space-y-2">
          <li>The grace period for free cancellation (24 hours after booking) only applies if your session is more than 48 hours away.</li>
          <li>Cancellation fees are calculated based on the time remaining until your scheduled session start time.</li>
          <li>All refunds are processed automatically upon cancellation.</li>
          <li>Refunds typically appear in your account within 5-10 business days, depending on your payment provider.</li>
          <li>For sessions paid with promotional credits or discounts, the refund will be adjusted proportionally.</li>
        </ul>

        <h3 className="text-lg font-semibold mt-6 mb-3">How to Cancel</h3>
        <ol className="list-decimal pl-5 space-y-2">
          <li>Log into your account and navigate to "My Sessions"</li>
          <li>Select the session you wish to cancel</li>
          <li>Click "Cancel Session" and provide a reason</li>
          <li>Review the cancellation fee (if applicable)</li>
          <li>Confirm your cancellation</li>
        </ol>

        <h3 className="text-lg font-semibold mt-6 mb-3">Exceptions</h3>
        <p>
          In cases of documented emergencies (medical emergencies, natural disasters, etc.), 
          please contact our support team at <a href="mailto:contact@guardly.app" className="text-blue-600 hover:underline">contact@guardly.app</a> for 
          special consideration.
        </p>
      </div>
    </div>
  );
};

export default CancellationPolicyNotice;